import React, { useMemo } from "react";
import DataTable from "react-data-table-component";

const SelectAllHeader = ({
  saveSelection,
  tableData,
  pageSelectionSet,
  onSelectAllToggle,
}) => {
  const isGlobalAll = saveSelection === "selected All";
  const allOnPageSelected =
    tableData.length > 0 &&
    tableData.every((row) => pageSelectionSet.has(String(row.id)));
  const checked = isGlobalAll || allOnPageSelected;
  const indeterminate =
    !isGlobalAll && pageSelectionSet.size > 0 && !allOnPageSelected;

  return (
    <input
      type="checkbox"
      className="qr-row-checkbox"
      aria-label="Select all students"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate;
      }}
      onChange={(e) => {
        e.stopPropagation();
        onSelectAllToggle();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const RowCheckbox = ({ row, pageSelectionSet, onRowToggle }) => {
  const isSelected = pageSelectionSet.has(String(row.id));
  return (
    <input
      type="checkbox"
      className="qr-row-checkbox"
      aria-label="Select row"
      checked={isSelected}
      onChange={(e) => {
        e.stopPropagation();
        onRowToggle(row, e.target.checked);
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const ContactButtons = ({ row, onStudentContact, onParentContact, onCounselorContact }) => {
  const hasParent = Boolean(row["20"]);
  return (
    <div
      className="contact-button-container"
      style={{
        display: "flex",
        flexDirection: "row",
        gap: "10px",
        padding: "4px",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onStudentContact({ row });
        }}
        className="contact-button"
        style={{
          backgroundColor: "#1395F2",
          color: "white",
          border: "2px solid #1395F2",
          boxShadow: "inset 0 0 0 2px #ffffff",
          borderRadius: "24px",
          padding: "6px 18px",
          fontSize: "14px",
          fontWeight: "500",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Student
      </button>
      {hasParent && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onParentContact({ row });
          }}
          className="contact-button"
          style={{
            backgroundColor: "#E36221",
            color: "white",
            border: "2px solid #E36221",
            boxShadow: "inset 0 0 0 2px #ffffff",
            borderRadius: "24px",
            padding: "6px 18px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Parent
        </button>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCounselorContact({ row });
        }}
        className="contact-button"
        style={{
          backgroundColor: "#009900",
          color: "white",
          border: "2px solid #009900",
          boxShadow: "inset 0 0 0 2px #ffffff",
          borderRadius: "24px",
          padding: "6px 18px",
          fontSize: "14px",
          fontWeight: "500",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Counselor
      </button>
    </div>
  );
};

const TagListCell = ({ row, field, expandedCells, toggleCellExpansion, emptyLabel, tagStyle }) => {
  const isExpanded = expandedCells[`${row.id}-${field}`];
  const raw = row[field];
  const items = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "string"
    ? raw.split(",")
    : [];
  const displayItems = isExpanded ? items : items.slice(0, 2);
  const hasMore = items.length > 2;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
      {displayItems.length > 0 ? (
        displayItems.map((item, idx) => (
          <span key={idx} style={tagStyle}>
            {item?.trim()}
          </span>
        ))
      ) : (
        <span style={{ color: "#9ca3af", fontSize: "12px", fontStyle: "italic" }}>
          {emptyLabel}
        </span>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleCellExpansion(row.id, field);
          }}
          style={{
            color: "#6b7280",
            fontSize: "11px",
            fontWeight: "500",
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
            padding: "2px 4px",
          }}
        >
          {isExpanded ? "Show less" : `+${items.length - 2} more`}
        </button>
      )}
    </div>
  );
};

const tableCustomStyles = {
  table: { style: { width: "max-content", minWidth: "100%" } },
  tableWrapper: {
    style: {
      display: "block",
      width: "max-content",
      minWidth: "100%",
      overflow: "visible",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottom: "2px solid #e2e8f0",
      minHeight: "44px",
      width: "max-content",
      minWidth: "100%",
    },
  },
  headCells: {
    style: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#374151",
      textTransform: "none",
      letterSpacing: "normal",
      padding: "8px 12px",
      whiteSpace: "nowrap",
      flexShrink: 0,
      borderBottom: "2px solid #e2e8f0",
    },
  },
  rows: {
    style: {
      minHeight: "56px",
      fontSize: "13px",
      borderBottom: "none",
      width: "max-content",
      minWidth: "100%",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#f3f4f6",
    },
    stripedStyle: {
      backgroundColor: "#fafafa",
    },
  },
  cells: {
    style: {
      padding: "8px 12px",
      flexShrink: 0,
      borderBottom: "1px solid #f1f5f9",
      backgroundColor: "inherit",
    },
  },
  pagination: { style: { display: "none" } },
};

const QueryResultTable = ({
  tableData,
  saveSelection,
  pageSelectionSet,
  onRowToggle,
  onSelectAllToggle,
  sortField,
  sortDirection,
  onSort,
  expandedCells,
  toggleCellExpansion,
  onStudentContact,
  onParentContact,
  onCounselorContact,
  selectedCount,
}) => {
  const columns = useMemo(
    () => [
      {
        name: (
          <SelectAllHeader
            saveSelection={saveSelection}
            tableData={tableData}
            pageSelectionSet={pageSelectionSet}
            onSelectAllToggle={onSelectAllToggle}
          />
        ),
        width: "52px",
        minWidth: "52px",
        sortable: false,
        cell: (row) => (
          <RowCheckbox
            row={row}
            pageSelectionSet={pageSelectionSet}
            onRowToggle={onRowToggle}
          />
        ),
      },
      {
        name: "Click to Contacts",
        minWidth: "380px",
        grow: 2,
        sortable: false,
        cell: (row) => (
          <ContactButtons
            row={row}
            onStudentContact={onStudentContact}
            onParentContact={onParentContact}
            onCounselorContact={onCounselorContact}
          />
        ),
      },
      {
        name: "Email Address",
        selector: (row) => row["0"] || "",
        sortField: "0",
        id: "0",
        sortable: true,
        minWidth: "260px",
        grow: 2,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#eff6ff",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              color: "#1e40af",
              fontWeight: "500",
            }}
          >
            {row["0"] || "No email"}
          </span>
        ),
      },
      {
        name: "First Name",
        selector: (row) => row["1"] || "",
        sortField: "1",
        id: "1",
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <span style={{ fontWeight: "600", color: "#059669", fontSize: "14px" }}>
            {row["1"] || "-"}
          </span>
        ),
      },
      {
        name: "Last Name",
        selector: (row) => row["2"] || "",
        sortField: "2",
        id: "2",
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <span style={{ fontWeight: "600", color: "#059669", fontSize: "14px" }}>
            {row["2"] || "-"}
          </span>
        ),
      },
      {
        name: "Ethnicity",
        selector: (row) => row["3"] || "",
        sortField: "3",
        id: "3",
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: row["3"] ? "#f0f9ff" : "#f9fafb",
              color: row["3"] ? "#0369a1" : "#6b7280",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {row["3"] || "Not specified"}
          </span>
        ),
      },
      {
        name: "Gender",
        selector: (row) => row["4"] || "",
        sortField: "4",
        id: "4",
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <span
            style={{
              backgroundColor:
                row["4"] === "Male" ? "#fef3f2" : row["4"] === "Female" ? "#f0f9ff" : "#f9fafb",
              color:
                row["4"] === "Male" ? "#dc2626" : row["4"] === "Female" ? "#2563eb" : "#6b7280",
              padding: "4px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {row["4"] || "Not specified"}
          </span>
        ),
      },
      {
        name: "Overall GPA",
        selector: (row) => row["5"] || 0,
        sortField: "5",
        id: "5",
        sortable: true,
        minWidth: "110px",
        cell: (row) => {
          const val = row["5"];
          return (
            <span
              style={{
                backgroundColor:
                  val >= 3.5 ? "#f0fdf4" : val >= 3.0 ? "#fffbeb" : val >= 2.5 ? "#fef2f2" : "#f9fafb",
                color:
                  val >= 3.5 ? "#059669" : val >= 3.0 ? "#d97706" : val >= 2.5 ? "#dc2626" : "#6b7280",
                padding: "4px 8px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
              }}
            >
              {val ? parseFloat(val).toFixed(2) : "0.00"}
            </span>
          );
        },
      },
      {
        name: "Athletics",
        sortField: "6",
        id: "6",
        sortable: true,
        minWidth: "220px",
        grow: 1,
        cell: (row) => (
          <TagListCell
            row={row}
            field="6"
            expandedCells={expandedCells}
            toggleCellExpansion={toggleCellExpansion}
            emptyLabel="No athletics"
            tagStyle={{
              backgroundColor: "#f0fdf4",
              color: "#059669",
              padding: "2px 6px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: "500",
            }}
          />
        ),
      },
      {
        name: "Activities",
        sortField: "7",
        id: "7",
        sortable: true,
        minWidth: "220px",
        grow: 1,
        cell: (row) => (
          <TagListCell
            row={row}
            field="7"
            expandedCells={expandedCells}
            toggleCellExpansion={toggleCellExpansion}
            emptyLabel="No activities"
            tagStyle={{
              backgroundColor: "#fef3f2",
              color: "#dc2626",
              padding: "2px 6px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: "500",
            }}
          />
        ),
      },
      {
        name: "Academics",
        sortField: "8",
        id: "8",
        sortable: true,
        minWidth: "220px",
        grow: 1,
        cell: (row) => (
          <TagListCell
            row={row}
            field="8"
            expandedCells={expandedCells}
            toggleCellExpansion={toggleCellExpansion}
            emptyLabel="No academics"
            tagStyle={{
              backgroundColor: "#eff6ff",
              color: "#2563eb",
              padding: "2px 6px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: "500",
            }}
          />
        ),
      },
      {
        name: "Interests",
        sortField: "9",
        id: "9",
        sortable: true,
        minWidth: "220px",
        grow: 1,
        cell: (row) => (
          <TagListCell
            row={row}
            field="9"
            expandedCells={expandedCells}
            toggleCellExpansion={toggleCellExpansion}
            emptyLabel="No interests"
            tagStyle={{
              backgroundColor: "#fef7ff",
              color: "#9333ea",
              padding: "2px 6px",
              borderRadius: "8px",
              fontSize: "11px",
              fontWeight: "500",
            }}
          />
        ),
      },
      {
        name: "School Name",
        selector: (row) => row["10"] || "",
        sortField: "10",
        id: "10",
        sortable: true,
        minWidth: "200px",
        grow: 1,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f9fafb",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              fontSize: "13px",
              color: "#374151",
            }}
          >
            {row["10"] || "No school specified"}
          </span>
        ),
      },
      {
        name: "District Name",
        selector: (row) => row["11"] || "",
        sortField: "11",
        id: "11",
        sortable: true,
        minWidth: "200px",
        grow: 1,
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#fff7ed",
              padding: "4px 8px",
              borderRadius: "6px",
              border: "1px solid #fed7aa",
              fontSize: "13px",
              color: "#374151",
            }}
          >
            {row["11"] || "No district specified"}
          </span>
        ),
      },
      {
        name: "Grade",
        selector: (row) => row["12"] || "",
        sortField: "12",
        id: "12",
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <span
            style={{
              backgroundColor: "#f0f9ff",
              color: "#0369a1",
              padding: "6px 12px",
              borderRadius: "12px",
              fontSize: "13px",
              fontWeight: "600",
              border: "1px solid #bae6fd",
            }}
          >
            {row["12"] ? `Grade ${row["12"]}` : "0"}
          </span>
        ),
      },
      {
        name: "Completed Fafsa",
        selector: (row) => row["15"],
        sortField: "15",
        id: "15",
        sortable: true,
        minWidth: "150px",
        cell: (row) => {
          const done = row["15"] === true || row["15"] === "true";
          return (
            <span
              style={{
                backgroundColor: done ? "#f0fdf4" : "#fef2f2",
                color: done ? "#059669" : "#dc2626",
                padding: "4px 8px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {done ? "✅ Completed" : "❌ Not Completed"}
            </span>
          );
        },
      },
    ],
    [
      tableData,
      saveSelection,
      pageSelectionSet,
      onRowToggle,
      onSelectAllToggle,
      expandedCells,
      toggleCellExpansion,
      onStudentContact,
      onParentContact,
      onCounselorContact,
    ]
  );

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => pageSelectionSet.has(String(row.id)),
        style: {
          backgroundColor: "#e0f2fe",
        },
      },
    ],
    [pageSelectionSet]
  );

  return (
    <div className="qr-rdt-wrapper">
      {selectedCount > 0 && (
        <div className="qr-selection-bar">
          {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
        </div>
      )}
      <div className="qr-table-scroll">
        <DataTable
          className="result-table"
          columns={columns}
          data={tableData}
          keyField="id"
          sortServer
          onSort={onSort}
          {...(sortField
            ? {
                defaultSortFieldId: sortField,
                defaultSortAsc: sortDirection === "asc",
              }
            : {})}
          pagination={false}
          customStyles={tableCustomStyles}
          conditionalRowStyles={conditionalRowStyles}
          dense
          persistTableHead
          noDataComponent={
            <div style={{ padding: "24px", color: "#6b7280" }}>No students found</div>
          }
        />
      </div>
    </div>
  );
};

export default QueryResultTable;
